import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LinkOutlined as LinkIcon, PeopleAltOutlined as PeopleIcon } from '@mui/icons-material';
import TablesService from '../../services/TablesService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import TabPanel from './TabPanel';
import WaiterSelect from './WaiterSelect';
import { CELL, contentSize, describeTablesError, elapsedLabel, fitScale, money } from './tablesUtils';

const REFRESH_MS = 30000;

const tableSx = (table, selected) => {
  const occupied = table.occupied;
  return {
    position: 'absolute',
    left: table.posX * CELL,
    top: table.posY * CELL,
    width: table.width * CELL,
    height: table.height * CELL,
    boxSizing: 'border-box',
    borderRadius: table.shape === 'ROUND' ? '50%' : '8px',
    border: '2px solid',
    borderColor: selected ? 'primary.main' : occupied ? 'warning.main' : 'success.main',
    bgcolor: occupied ? 'warning.light' : 'background.paper',
    color: occupied ? 'warning.contrastText' : 'text.primary',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    overflow: 'hidden',
    textAlign: 'center',
    boxShadow: 1,
    '&:hover': { boxShadow: 4 },
  };
};

const ServiceFloor = ({ locationId, areas, perms, onReload }) => {
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [openTabId, setOpenTabId] = useState(null);
  const [openTable, setOpenTable] = useState(null); // mesa libre elegida para abrir
  const [guests, setGuests] = useState('');
  const currentUserEmail = localStorage.getItem('userEmail') || '';
  const [waiterEmail, setWaiterEmail] = useState(currentUserEmail);
  const [opening, setOpening] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  const area = useMemo(() => areas.find((item) => item.id === selectedAreaId) || areas[0] || null, [areas, selectedAreaId]);
  const size = useMemo(() => contentSize(area?.tables || []), [area]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Refresco periódico: otros mozos y otras cajas operan las mismas mesas.
  useEffect(() => {
    const timer = setInterval(() => onReload({ silent: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [onReload]);

  // Escala el plano para que entre en el ancho disponible (tablets/pantallas chicas).
  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const available = element.clientWidth;
      setScale(fitScale(available, size.cols));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [size.cols]);

  const tablesById = useMemo(() => new Map(areas.flatMap((a) => a.tables).map((t) => [t.id, t])), [areas]);

  const handleTableClick = (table) => {
    if (table.tab) {
      setOpenTabId(table.tab.tabId);
      return;
    }
    if (table.linkedToTableId) {
      const primary = tablesById.get(table.linkedToTableId);
      if (primary?.tab) setOpenTabId(primary.tab.tabId);
      return;
    }
    if (!perms.operate) {
      SnackbarUtils.info('No tenés permiso para abrir mesas.');
      return;
    }
    setGuests('');
    setWaiterEmail(currentUserEmail);
    setOpenTable(table);
  };

  const confirmOpen = async () => {
    setOpening(true);
    try {
      const tab = await TablesService.openTab(openTable.id, { guests: guests === '' ? null : Number(guests), waiterEmail: waiterEmail || null });
      setOpenTable(null);
      await onReload({ silent: true });
      setOpenTabId(tab.id);
    } catch (error) {
      SnackbarUtils.error(describeTablesError(error));
      onReload({ silent: true });
      setOpenTable(null);
    } finally {
      setOpening(false);
    }
  };

  const handlePanelClose = React.useCallback(() => setOpenTabId(null), []);
  const handleChanged = React.useCallback(() => onReload({ silent: true }), [onReload]);

  if (!area) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Esta sucursal todavía no tiene mesas</Typography>
        <Typography color="text.secondary">Pedile a un encargado que arme el plano desde el modo Editor.</Typography>
      </Paper>
    );
  }

  const occupiedCount = areas.flatMap((a) => a.tables).filter((t) => t.occupied).length;
  const totalCount = areas.flatMap((a) => a.tables).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Tabs value={area.id} onChange={(_, value) => setSelectedAreaId(value)} variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1, minWidth: 0 }}>
          {areas.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={`${item.name} (${item.tables.filter((t) => t.occupied).length}/${item.tables.length})`}
            />
          ))}
        </Tabs>
        <Chip size="small" label={`${occupiedCount} de ${totalCount} ocupadas`} />
      </Stack>

      <Paper variant="outlined" ref={containerRef} sx={{ overflow: 'auto' }}>
        <Box sx={{ width: size.cols * CELL * scale, height: size.rows * CELL * scale }}>
          <Box
            data-testid="service-canvas"
            sx={{
              position: 'relative',
              width: size.cols * CELL,
              height: size.rows * CELL,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {area.tables.map((table) => (
              <Box key={table.id} data-testid={`service-table-${table.id}`} sx={tableSx(table, table.tab?.tabId === openTabId)} onClick={() => handleTableClick(table)}>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.1} noWrap sx={{ maxWidth: '92%' }}>
                  {table.name}
                </Typography>
                {table.tab ? (
                  <>
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: '92%' }}>
                      {money(table.tab.pendingTotal, table.tab.currency)}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ maxWidth: '92%', opacity: 0.85 }}>
                      {elapsedLabel(table.tab.openedAt, now)}
                    </Typography>
                  </>
                ) : table.linkedToTableId ? (
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <LinkIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption">{tablesById.get(table.linkedToTableId)?.name}</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ opacity: 0.7 }}>
                    <PeopleIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption">{table.capacity}</Typography>
                  </Stack>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: '3px', border: '2px solid', borderColor: 'success.main' }} />
          <Typography variant="caption">Libre</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'warning.light', border: '2px solid', borderColor: 'warning.main' }} />
          <Typography variant="caption">Ocupada (importe pendiente y tiempo abierta)</Typography>
        </Stack>
      </Stack>

      <Dialog open={Boolean(openTable)} onClose={() => setOpenTable(null)} fullWidth maxWidth="xs">
        <DialogTitle>Abrir mesa {openTable?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="number"
            label={`Comensales (capacidad ${openTable?.capacity ?? ''})`}
            value={guests}
            inputProps={{ min: 1, max: 99 }}
            onChange={(event) => setGuests(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && !opening && confirmOpen()}
          />
          <Box sx={{ mt: 2 }}>
            <WaiterSelect locationId={locationId} value={waiterEmail} onChange={setWaiterEmail} extraEmails={[currentUserEmail]} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTable(null)}>Cancelar</Button>
          <Button variant="contained" disabled={opening} onClick={confirmOpen}>Abrir</Button>
        </DialogActions>
      </Dialog>

      {openTabId && (
        <TabPanel
          key={openTabId}
          tabId={openTabId}
          areas={areas}
          locationId={locationId}
          perms={perms}
          onClose={handlePanelClose}
          onChanged={handleChanged}
        />
      )}
    </Box>
  );
};

export default ServiceFloor;
